import type { AgentTool } from './tools';
import { topByRelevance } from './relevance';

/**
 * MCP bridge — adapt a Model Context Protocol tool list into the agent's AgentTool[],
 * so any MCP server's tools become first-class agent tools (edge-safe: you supply the
 * transport via `callTool`; this module has no node/network dependency of its own).
 *
 * Pass the server's advertised tools + a `callTool(name, args)` that performs the call
 * (over stdio/HTTP/whatever you wire up); each becomes an AgentTool the Agent can use.
 */
export interface McpToolSpec {
  name: string;
  description?: string;
  /** JSON Schema for the tool's arguments (MCP's `inputSchema`). */
  inputSchema?: object;
}

/** Perform an MCP tool call; return its textual result. Throw to surface an error to the model. */
export type McpCall = (name: string, args: any) => Promise<unknown>;

export interface McpImage { mimeType: string; data: string }
export interface McpToolResult { text: string; images?: McpImage[] }

/** Normalize an MCP call result into text + optional image blocks. */
function toResult(result: unknown): McpToolResult {
  if (result == null) return { text: '' };
  if (typeof result === 'string') return { text: result };
  const content = (result as any).content;
  if (Array.isArray(content)) {
    const texts: string[] = [];
    const images: McpImage[] = [];
    for (const c of content) {
      if (c?.type === 'image' && typeof c.data === 'string' && c.mimeType) {
        images.push({ mimeType: c.mimeType, data: c.data });
      } else if (typeof c?.text === 'string') {
        texts.push(c.text);
      } else {
        texts.push(JSON.stringify(c));
      }
    }
    const text = texts.join('\n');
    if (text || images.length) return { text, ...(images.length ? { images } : {}) };
  }
  return { text: JSON.stringify(result) };
}

function toText(result: unknown): string { return toResult(result).text; }

/** Adapt one MCP tool spec into an AgentTool backed by `callTool`. */
export function mcpToolToAgentTool(spec: McpToolSpec, callTool: McpCall, prefix = 'mcp__'): AgentTool {
  return {
    name: `${prefix}${spec.name}`,
    description: spec.description ?? `MCP tool ${spec.name}`,
    parameters: spec.inputSchema ?? { type: 'object', properties: {} },
    async run(args, _ctx) {
      const r = toResult(await callTool(spec.name, args ?? {}));
      return r.images?.length ? r : r.text;
    },
  };
}

/** Adapt a whole MCP tool list into AgentTool[] (names prefixed to avoid collisions).
 *  Optional `filter` pre-narrows the set at mount time (host allowlist), so a server with
 *  hundreds of tools needn't mount them all eagerly. */
export function mcpToolsToAgentTools(specs: McpToolSpec[], callTool: McpCall, prefix = 'mcp__', filter?: (spec: McpToolSpec) => boolean): AgentTool[] {
  return (filter ? specs.filter(filter) : specs).map((s) => mcpToolToAgentTool(s, callTool, prefix));
}

export interface McpToolSearchOptions {
  /** Prefix stripped from / shown on tool names (cosmetic; mirrors the adapter prefix). Default 'mcp__'. */
  prefix?: string;
  /** Max tools returned per `ToolSearch` query. Default 10. */
  maxResults?: number;
}

/** Render one spec for the search result: name, description, and its argument schema (so the
 *  model knows how to call it via `McpCall`). */
function describeSpec(s: McpToolSpec): string {
  const schema = s.inputSchema ? `\n   args: ${JSON.stringify(s.inputSchema)}` : '';
  return `${s.name} — ${s.description ?? '(no description)'}${schema}`;
}

/**
 * Deferred-mount mode (ToolSearch-equivalent) for large MCP tool sets. Instead of mounting N
 * tools into the wire schema (cost + latency + model confusion past a few dozen), mount exactly
 * TWO bounded tools regardless of N:
 *   - `ToolSearch({ query })` — ranks the catalog by relevance and returns the top matches with
 *      their argument schemas, so the model discovers what's available on demand.
 *   - `McpCall({ name, args })` — invokes any catalog tool by name through the same transport.
 * Keep `mcpToolsToAgentTools` (eager) as the default for small sets.
 */
export function makeMcpToolSearch(specs: McpToolSpec[], callTool: McpCall, options: McpToolSearchOptions = {}): AgentTool[] {
  const maxResults = options.maxResults ?? 10;
  const byName = new Map(specs.map((s) => [s.name, s]));
  const catalogLine = `${specs.length} MCP tool(s) available — search by keyword, then call by exact name.`;

  const searchTool: AgentTool = {
    name: 'ToolSearch',
    description: `Search the available MCP tools by keyword (${catalogLine}). Returns matching tool names + their argument schemas; call one with \`McpCall\`.`,
    parameters: { type: 'object', required: ['query'], properties: { query: { type: 'string', description: 'keywords to match against tool name + description' } } },
    async run({ query }) {
      const q = String(query ?? '').trim();
      if (!q) return catalogLine;
      const { kept } = topByRelevance(specs, q, (s) => `${s.name} ${s.description ?? ''}`, maxResults);
      if (!kept.length) return `(no MCP tool matches "${q}" — try broader keywords)`;
      return kept.map(describeSpec).join('\n');
    },
  };

  const callMcpTool: AgentTool = {
    name: 'McpCall',
    description: 'Call an MCP tool discovered via `ToolSearch`, by its exact name. Pass its arguments as `args`.',
    parameters: {
      type: 'object',
      required: ['name'],
      properties: {
        name: { type: 'string', description: 'exact tool name from ToolSearch' },
        args: { type: 'object', description: 'arguments object for the tool (per its schema)' },
      },
    },
    async run({ name, args }) {
      const n = String(name ?? '');
      if (!byName.has(n)) return `Error: unknown MCP tool '${n}'. Use ToolSearch to find valid names.`;
      const r = toResult(await callTool(n, args ?? {}));
      return r.images?.length ? r : r.text;
    },
  };

  return [searchTool, callMcpTool];
}

/** Minimal shape of a mounted MCP server this module needs — structurally satisfied by
 *  `MountedMcp` from `mcp.client.ts`, kept local so `mcp.ts` stays node-free/edge-safe. */
export interface MountedMcpLike {
  name: string;
  specs: McpToolSpec[];
  client: { callTool(name: string, args: unknown): Promise<unknown> };
}

/** A flattened catalog entry's origin: which server owns it + the un-prefixed raw tool name. */
export interface McpRoute { server: string; rawName: string }

/** Flatten servers' specs into one `mcp__<server>__<tool>` namespace + a display→origin map.
 *  Sanitizing/truncating to provider name rules can MERGE distinct names (`a/b` & `a:b` → `a_b`),
 *  so dedupe with a numeric suffix — a silent overwrite would orphan a tool. */
export function buildMcpCatalog(servers: { name: string; specs: McpToolSpec[] }[]): { specs: McpToolSpec[]; routes: Map<string, McpRoute> } {
  const specs: McpToolSpec[] = [];
  const routes = new Map<string, McpRoute>();
  for (const m of servers) {
    for (const s of m.specs) {
      const base = `mcp__${m.name}__${s.name}`.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 128);
      let display = base;
      for (let i = 2; routes.has(display); i++) display = `${base.slice(0, 128 - String(i).length - 1)}_${i}`;
      specs.push({ name: display, description: s.description, inputSchema: s.inputSchema });
      routes.set(display, { server: m.name, rawName: s.name });
    }
  }
  return { specs, routes };
}

/** Resolve a routed MCP call to its result. Throw to surface an error to the model. */
export type McpRouteResolver = (server: string, rawName: string, args: any) => Promise<unknown>;

/** Shared core: wire the `ToolSearch`/`McpCall` pair over a prebuilt catalog, dispatching each
 *  call through `resolve(server, rawName, args)` (eager → a mounted client; lazy → connect-on-call). */
function searchOverCatalog(servers: string[], specs: McpToolSpec[], routes: Map<string, McpRoute>, resolve: McpRouteResolver, options?: McpToolSearchOptions) {
  const tools = specs.length
    ? makeMcpToolSearch(specs, (name, args) => {
        const r = routes.get(name);
        if (!r) throw new Error(`unknown MCP tool '${name}' — use ToolSearch to find valid names`);
        return resolve(r.server, r.rawName, args ?? {});
      }, options)
    : [];
  return { tools, serverNames: servers, toolCount: specs.length };
}

/**
 * Ergonomic deferred-mount over already-mounted MCP servers: flattens their specs into one
 * `mcp__<server>__<tool>` namespace and wires a single `ToolSearch`/`McpCall` pair that routes
 * each call to the owning server's RAW `callTool` (so result normalization happens exactly once —
 * double-normalizing corrupts image blocks).
 */
export function makeMcpToolSearchFromMounted(
  mounted: MountedMcpLike[],
  options?: McpToolSearchOptions,
): { tools: AgentTool[]; serverNames: string[]; toolCount: number } {
  const { specs, routes } = buildMcpCatalog(mounted);
  const byName = new Map(mounted.map((m) => [m.name, m]));
  return searchOverCatalog(mounted.map((m) => m.name), specs, routes, (server, rawName, args) => byName.get(server)!.client.callTool(rawName, args), options);
}

/**
 * Lazy variant: same `ToolSearch`/`McpCall` surface, but the server isn't required to be connected.
 * Each `McpCall` resolves the owning server from the catalog and `resolve(server, rawName, args)`
 * connects-on-demand — so a turn that calls no MCP tool opens ZERO connections. Edge-safe: the
 * caller (`mountMcpCatalog` in `mcp.client.ts`) supplies the node-side connect/pool logic.
 */
export function makeLazyMcpToolSearch(
  servers: { name: string; specs: McpToolSpec[] }[],
  resolve: McpRouteResolver,
  options?: McpToolSearchOptions,
): { tools: AgentTool[]; serverNames: string[]; toolCount: number } {
  const { specs, routes } = buildMcpCatalog(servers);
  return searchOverCatalog(servers.map((s) => s.name), specs, routes, resolve, options);
}
